class SubscribePopup {
  static #SESSION_KEY = 'rr_subscribe_popup_hidden';
  static #DELAY_MS = 20000;

  constructor(element) {
    this.root = element;
    this.backdrop = this.root.querySelector('[data-subscribe-pop-up-backdrop]');
    this.closeBtn = this.root.querySelector('[data-subscribe-pop-up-close]');
    this.form = this.root.querySelector('.subscribe-pop-up__form');
    this.input = this.root.querySelector('.subscribe-pop-up__input');
    this.status = this.root.querySelector('.subscribe-pop-up__status');
    this.isSubmitting = false;
    this.timerId = null;

    if (!this.form || !this.input) {
      return;
    }

    if (this.#isHiddenForSession()) {
      return;
    }

    this.#bindEvents();
    this.#scheduleOpen();
  }

  #scheduleOpen() {
    this.timerId = window.setTimeout(() => {
      this.open();
    }, SubscribePopup.#DELAY_MS);
  }

  open() {
    this.root.classList.add('subscribe-pop-up--visible');
    this.root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('subscribe-pop-up-open');
  }

  close() {
    if (this.timerId) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }

    this.root.classList.remove('subscribe-pop-up--visible');
    this.root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('subscribe-pop-up-open');
    this.#rememberHiddenForSession();
  }

  #bindEvents() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.root.classList.contains('subscribe-pop-up--visible')) {
        this.close();
      }
    });

    this.form.addEventListener('submit', (event) => this.#handleSubmit(event));
  }

  #setStatus(message, type = 'info') {
    if (!this.status) return;

    this.status.textContent = message;
    this.status.classList.remove(
      'subscribe-pop-up__status--error',
      'subscribe-pop-up__status--success',
    );

    if (type === 'error') {
      this.status.classList.add('subscribe-pop-up__status--error');
    } else if (type === 'success') {
      this.status.classList.add('subscribe-pop-up__status--success');
    }
  }

  #validate() {
    const value = this.input.value.trim();

    if (!value) {
      this.#setStatus('Email is required', 'error');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      this.#setStatus('Invalid email format', 'error');
      return false;
    }

    this.#setStatus('');
    return true;
  }

  async #handleSubmit(event) {
    event.preventDefault();

    if (this.isSubmitting) {
      return;
    }

    if (!this.#validate()) {
      return;
    }

    if (typeof window.backendData === 'undefined' || !window.backendData.ajaxUrl) {
      this.#setStatus('Configuration error', 'error');
      return;
    }

    this.isSubmitting = true;
    this.form.classList.add('subscribe-pop-up__form--loading');
    this.#setStatus('Subscribing...');

    const formData = new FormData();
    formData.append('action', 'subscribe_newsletter');
    formData.append('email', this.input.value.trim());
    formData.append('nonce', window.backendData.nonce || '');

    try {
      const response = await fetch(window.backendData.ajaxUrl, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        this.#setStatus(data.data?.message || 'Thank you for subscribing!', 'success');
        this.form.reset();
        this.#rememberHiddenForSession();
      } else {
        this.#setStatus(
          data.data?.message || 'Subscription failed. Please try again.',
          'error',
        );
      }
    } catch (error) {
      this.#setStatus('Network error. Please try again.', 'error');
    } finally {
      this.isSubmitting = false;
      this.form.classList.remove('subscribe-pop-up__form--loading');
    }
  }

  #isHiddenForSession() {
    try {
      return window.sessionStorage.getItem(SubscribePopup.#SESSION_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  #rememberHiddenForSession() {
    try {
      window.sessionStorage.setItem(SubscribePopup.#SESSION_KEY, '1');
    } catch (error) {
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const popupElement = document.querySelector('[data-subscribe-pop-up]');
  if (popupElement) {
    new SubscribePopup(popupElement);
  }
});

